import { useMemo } from 'react';
import { SVG_SIZE_M } from 'src/constants';
import {
  useComparingConversationsParams,
  useDownloadFile,
  useGetUser,
  useImageMaskingIsEditingMode,
} from 'src/hooks';
import {
  DownloadSimple,
  MagicWand,
  SlidersHorizontal,
} from '@phosphor-icons/react';
import { Button } from 'src/v2/commonComponents/Button';
import styles from './ThreadMediaFooter.module.scss';
import { CopyMessageButton } from '../CopyMessageButton';
import { ShareButton } from 'src/components/ShareButton';
import { TaskState } from 'src/types';
import { MoreMessageActionsTool } from '../MoreMessageActionsTool';
import { Message } from 'src/types';

interface ThreadMediaFooterProps {
  message: Message;
  isEditDisabled?: boolean;
  url: string;
  isStreaming?: boolean;
  isExpandedImageGenSettings?: boolean;
  taskState?: TaskState;
  onEditClick?: () => void;
  setModifyPanelExpanded: (value: boolean) => void;
}

// TODO(olha): move each button to separate component
export const ThreadMediaFooter = ({
  message,
  url,
  isStreaming,
  isExpandedImageGenSettings,
  taskState,
  setModifyPanelExpanded,
  onEditClick,
  isEditDisabled,
}: ThreadMediaFooterProps) => {
  const { isOpenTier, isFreeTier } = useGetUser();
  const { onDownloadFile } = useDownloadFile();
  const { isEditingMode } = useImageMaskingIsEditingMode();
  const { isCompareMode } = useComparingConversationsParams();

  const handleDownloadClick = () => {
    if (!url) {
      return;
    }
    onDownloadFile(url);
  };

  const disabledTaskState = taskState !== TaskState.DONE;

  const shouldDisableSharing = useMemo(() => {
    if (isOpenTier || isFreeTier) {
      return true;
    }

    if (isStreaming || disabledTaskState) {
      return true;
    }
    return false;
  }, [isOpenTier, isFreeTier, isStreaming, disabledTaskState]);

  const handleRegenerateClick = () => {
    setModifyPanelExpanded(!isExpandedImageGenSettings);
  };

  if (isStreaming) {
    return null;
  }

  return (
    <div className={styles.root}>
      {onEditClick && (
        <Button
          type="button"
          onClick={onEditClick}
          color="secondary-outline"
          disabled={
            isEditDisabled ||
            isOpenTier ||
            isEditingMode ||
            isStreaming ||
            disabledTaskState
          }
        >
          <MagicWand size={SVG_SIZE_M} />
          Edit image
        </Button>
      )}

      {!isStreaming && (
        <Button
          color="secondary-outline"
          disabled={isCompareMode || disabledTaskState}
          onClick={handleRegenerateClick}
        >
          <SlidersHorizontal size={SVG_SIZE_M} />
          Regenerate
        </Button>
      )}

      <Button
        type="button"
        onClick={handleDownloadClick}
        color="secondary-outline"
        disabled={
          isEditDisabled || isOpenTier || isStreaming || disabledTaskState
        }
      >
        <DownloadSimple size={SVG_SIZE_M} />
        Download
      </Button>

      <CopyMessageButton
        withTitle
        withBackground
        disabled={true}
        withTooltip={false}
      />

      <ShareButton
        withTitle
        messageId={message.message_id}
        disabled={shouldDisableSharing}
        withTooltip={false}
      />

      <MoreMessageActionsTool message={message} disabled={isStreaming} />
    </div>
  );
};
